import { useMemo } from "react";
import { Polygon, Tooltip } from "react-leaflet";
import type { LatLngTuple } from "leaflet";
import { useQuery } from "@tanstack/react-query";

import { telemetryAjax } from "../../ajax/TelemetryAjax";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { DigBlockFenceDTO } from "../../DTOs/Telemetry/DigBlockFenceDTO";
import { MINIMUM_VERTICES, parseGeofencePolygon } from "./geofencePolygon";
import { StackedGroup } from "./LayerStack";
import { LAYER_RANK } from "./layerStackOrder";
import { blastPatternTooltipsByMaster, digBlockPathOptions, digBlockTooltip, topLiveFlitchBlocks } from "./mapStyles";
import { useBlastPatternFences } from "./useBlastPatternFences";

/**
 * Draws the LIVE dig blocks - the ones still being mined - beneath the fleet, coloured by material class.
 *
 * Note: WHY BOTH LAYERS MATTER TOGETHER. Ore Control own the dig-block outlines and Drill & Blast own the
 * blast-pattern ones, and the two are drawn independently on purpose - comparing them is the reconciliation
 * the Ore Tally reports numerically. Seeing them on one map, over where the machines actually drove, is the
 * same comparison made visible.
 *
 * Note: "live" is decided by the SERVER (Completed = false), not here. Finished blocks accumulate forever -
 * 6,740 blocks against 67 not completed on the test database - so filtering client-side would mean shipping
 * the whole history down the wire to throw almost all of it away.
 *
 * Note: only the top live flitch is drawn, as the server flags it. A flitch is a vertical sub-bench stacked
 * over the same plan-view ground, so the flitches below share its footprint; drawing them too would stack
 * outlines exactly on top of each other and bury the exposed ground's class colour under the one beneath.
 *
 * Note: the tooltip names the block's blast pattern too. The blocks tile a fired pattern and draw above it,
 * so they take the hover from almost all of it, and the pattern's reference and status would be lost.
 */

export const DIG_BLOCK_FENCE_QUERY_KEY = ["telemetry", "digBlockFences"];

type DigBlockFenceLayerProps = {
  visible: boolean;
};

export const DigBlockFenceLayer: React.FC<DigBlockFenceLayerProps> = ({ visible }) => {
  const { data: fences = [] } = useQuery({
    queryKey: DIG_BLOCK_FENCE_QUERY_KEY,
    queryFn: async () => {
      const res = await telemetryAjax.getLiveDigBlockFences();
      // Note: this endpoint returns the array directly rather than a PagedResult, matching the blast
      // pattern fences - so the error guard is the only unwrapping needed.
      if (ErrorResponse.isErrorResponse(res)) return [] as DigBlockFenceDTO[];
      return res;
    },
    staleTime: 5 * 60 * 1000,
  });

  const patterns = useBlastPatternFences();
  const patternTooltips = useMemo(() => blastPatternTooltipsByMaster(patterns), [patterns]);

  const shapes = useMemo(() => topLiveFlitchBlocks(fences).flatMap((fence: DigBlockFenceDTO) => {
    const { vertices } = parseGeofencePolygon(fence.geofencePolygon);

    // Note: a polygon that will not parse is skipped rather than drawn wrong. The server logs it by block
    // id when inference hits the same row, so it is diagnosable there rather than here.
    if (vertices.length < MINIMUM_VERTICES) return [];

    return [{
      id: fence.geoOreBlockId,
      blastMasterId: fence.blastMasterId,
      positions: vertices.map(v => [v.lat, v.lng] as LatLngTuple),
      pathOptions: digBlockPathOptions(fence.materialClass, fence.isWaste),
      tooltip: digBlockTooltip(fence),
    }];
  }), [fences]);

  if (!visible) return null;

  return (
    <StackedGroup rank={LAYER_RANK.blocks} version={shapes}>
      {shapes.map(shape => {
        const patternTooltip = shape.blastMasterId == null ? undefined : patternTooltips.get(shape.blastMasterId);

        return (
          <Polygon key={`digblock-${shape.id}`} positions={shape.positions} pathOptions={shape.pathOptions}>
            <Tooltip>
              <div>{shape.tooltip}</div>
              {patternTooltip && <div>{patternTooltip}</div>}
            </Tooltip>
          </Polygon>
        );
      })}
    </StackedGroup>
  );
};

export default DigBlockFenceLayer;
